/**
 * 付费转化看板（ADM-MTR-S012）共享契约：GET /api/v1/metrics/paywall-conversion 的查询参数与响应形态。
 *
 * 四级人数口径（按 UTC 日 × 用户去重）：
 * - 活跃 = 当日在自建埋点 sink 有任意事件（仅同意分析采集的用户会上报）
 * - 进付费面板 = `paywall_product_view`；创建付费订单 = 原生 `store_purchase_attempt` + Web `web_purchase_attempt`（点击购买）
 * - 付费 = `payment_settlements` 当日有到账结算（权威交易事实，含 IAP、Stripe 与已登记框架通道）
 * 「支付方式」取结算通道层（`SETTLEMENT_CHANNELS`：IAP / STRIPE / 已登记框架通道）：埋点 `payment_method` 经
 * `channel_of()` 归并——目录行取其 `channel`（如 PAYERMAX_VISA → PAYERMAX），目录外的 Stripe 同步取值归 STRIPE；
 * Web 下单埋点不带支付方式或取值在登记值域外时归 null 分组、不被 `payment_channels` 筛选命中（AD §14.2）。
 */

import { z } from 'zod';
import { SettlementChannelSchema } from './payment-methods';

/** 分组 / 筛选维度闭集 */
export const PAYWALL_CONVERSION_DIMENSIONS = [
  'date',
  'app',
  'language',
  'channel',
  'platform',
  'app_version',
  'scenario',
  'payment_channel',
  'user_type',
  'payer_status',
] as const;
export const paywallConversionDimensionSchema = z.enum(PAYWALL_CONVERSION_DIMENSIONS);
export type PaywallConversionDimension = z.infer<typeof paywallConversionDimensionSchema>;

/** 单次查询最多跨 92 天（埋点原始行保留 90 天，更长窗口无数据可读） */
export const PAYWALL_CONVERSION_MAX_DAYS = 92;

/** 埋点原始行保留天数（dev-docs 99 §3.5.4：90 天硬删），更早的日期没有活跃 / 面板 / 下单数据 */
export const ANALYTICS_RAW_RETENTION_DAYS = 90;

/** 可查询的最早 UTC 日：早于它的窗口只剩到账数据，前三级恒 0，会误导转化率 */
export function earliestPaywallConversionDay(now: Date): string {
  const today = Date.parse(`${now.toISOString().slice(0, 10)}T00:00:00Z`);
  return new Date(today - (ANALYTICS_RAW_RETENTION_DAYS - 1) * 86_400_000)
    .toISOString()
    .slice(0, 10);
}
/** 分组行返回上限；超出时 `truncated=true` */
export const PAYWALL_CONVERSION_MAX_ROWS = 5000;

/** YYYY-MM-DD 且必须是真实日历日：Date.parse 会把 2026-02-31 规范化成 03-03，须回写比对 */
export const metricsCalendarDay = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, '日期须为 YYYY-MM-DD')
  .refine((value) => {
    const parsed = Date.parse(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed) && new Date(parsed).toISOString().slice(0, 10) === value;
  }, '日期无效');

/**
 * 多值筛选走重复查询参数（`?channels=a&channels=b`），每个值原样保留、不按逗号拆分：
 * 渠道 / 版本 / 商城类型是自由文本，可能自带逗号。去空白、去重后最多 50 个。
 */
export const metricsRepeatedListParam = <T extends string>(item: z.ZodType<T, string>) =>
  z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((raw) => [
      ...new Set(
        (raw === undefined ? [] : Array.isArray(raw) ? raw : [raw])
          .map((value) => value.trim())
          .filter(Boolean),
      ),
    ])
    .pipe(z.array(item).max(50));

/** 以重复查询参数传递的多值参数名（路由按此把同名参数收成数组） */
export const PAYWALL_CONVERSION_LIST_PARAMS = [
  'app_ids',
  'languages',
  'channels',
  'platforms',
  'app_versions',
  'scenarios',
  'payment_channels',
] as const;

export const paywallConversionQuerySchema = z
  .object({
    from: metricsCalendarDay,
    to: metricsCalendarDay,
    group_by: z
      .string()
      .optional()
      .transform((raw) =>
        raw === undefined || raw.trim() === ''
          ? ['date']
          : [
              ...new Set(
                raw
                  .split(',')
                  .map((value) => value.trim())
                  .filter(Boolean),
              ),
            ],
      )
      // 只有分隔符（如 `,,,`）时同缺省处理，不产生「零维度只出汇总」的未定义形态
      .transform((dims) => (dims.length ? dims : ['date']))
      .pipe(z.array(paywallConversionDimensionSchema).max(PAYWALL_CONVERSION_DIMENSIONS.length)),
    app_ids: metricsRepeatedListParam(z.string().uuid()).transform((ids) =>
      ids.map((id) => id.toLowerCase()),
    ),
    languages: metricsRepeatedListParam(z.string().max(35)),
    channels: metricsRepeatedListParam(z.string().max(256)),
    platforms: metricsRepeatedListParam(z.enum(['IOS', 'ANDROID', 'WEB'])),
    app_versions: metricsRepeatedListParam(z.string().max(32)),
    scenarios: metricsRepeatedListParam(z.string().max(64)),
    // 结算通道层（AD「payment_channels」= 70 §3 SETTLEMENT_CHANNELS，含已登记框架通道）；支付方式层取值仍拒
    payment_channels: metricsRepeatedListParam(SettlementChannelSchema),
    user_type: z.enum(['new', 'returning']).optional(),
    payer_status: z.enum(['payer', 'non_payer']).optional(),
    min_active_users: z.coerce.number().int().min(0).max(100_000_000).optional(),
  })
  // 拒绝未声明参数：如把 app_ids 拼成 app_id 时静默丢弃会返回看似合理的全 App 数据
  .strict()
  .superRefine((query, ctx) => {
    const span =
      (Date.parse(`${query.to}T00:00:00Z`) - Date.parse(`${query.from}T00:00:00Z`)) / 86_400_000;
    if (span < 0) ctx.addIssue({ code: 'custom', path: ['to'], message: 'to 不得早于 from' });
    else if (span + 1 > PAYWALL_CONVERSION_MAX_DAYS)
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `时间窗最多 ${PAYWALL_CONVERSION_MAX_DAYS} 天`,
      });
    // 按商城类型 / 支付方式分组时活跃人数不可归因，门槛无从比较：显式拒绝而不是静默忽略
    if (
      query.min_active_users !== undefined &&
      query.group_by.some(
        (dimension) => dimension === 'scenario' || dimension === 'payment_channel',
      )
    )
      ctx.addIssue({
        code: 'custom',
        path: ['min_active_users'],
        message: '按商城类型或支付方式分组时不支持活跃人数门槛',
      });
  });
export type PaywallConversionQuery = z.infer<typeof paywallConversionQuerySchema>;

/** 分组键：只含请求的 group_by 维度；值为 null = 未知 / 未上报 */
export type PaywallConversionDimensionValues = Partial<
  Record<PaywallConversionDimension, string | null>
>;

/**
 * 单行指标。`null` = 该指标在当前分组下不可归因：按商城类型分组时活跃人数不可拆；
 * 按支付方式分组时活跃 / 进付费面板人数不可拆（付费面板展示不区分支付方式）。
 */
export type PaywallConversionMetrics = {
  active_users: number | null;
  paywall_users: number | null;
  order_users: number;
  paid_users: number;
  /** 完成付费平均耗时（毫秒）：仅应用内支付路径 `store_purchase_timing` 且到账观察成功的样本 */
  avg_pay_duration_ms: number | null;
  avg_pay_duration_samples: number;
};

export type PaywallConversionRow = {
  dims: PaywallConversionDimensionValues;
} & PaywallConversionMetrics;

export type PaywallConversionData = {
  window: { from: string; to: string; timezone: 'UTC' };
  group_by: PaywallConversionDimension[];
  /** 全窗口汇总：各级人数按整个窗口去重（不等于逐日相加） */
  summary: PaywallConversionMetrics;
  rows: PaywallConversionRow[];
  truncated: boolean;
  /** 筛选下拉候选值（窗口内实际出现过的值） */
  options: {
    apps: { id: string; name: string; platform: string | null }[];
    languages: string[];
    channels: string[];
    app_versions: string[];
    scenarios: string[];
  };
  /** ARPU / ARPPU 依赖联合 USD 收入发布，当前发布口径不支持本看板维度 → 恒 PENDING */
  revenue: { status: 'PENDING_INSTRUMENTATION'; reason: string };
  generated_at: string;
};
